import { useEffect } from "react";
import TourUploadButton from "./PhotoUploadButton";
import AlertMessage from "../AlertMessage";
import { triggerAnalyticsEvent } from "../../lib";

interface PhotoRejectProps {
  onRetake: (event: React.ChangeEvent<HTMLInputElement>) => void;
  photoModerationParams: {
    photoModerationError?: object | null;
    photoDenyReasons?: string[] | null;
    photoModResponse?: {
      photoModeration?: {
        results?: string;
      };
    } | null;
  };
  children?: React.ReactNode;
}

/**
 * Component shown when a photo is rejected by the moderation service
 * - Displays appropriate error messaging and offers a retry option
 * - Tracks rejection analytics with detailed rejection reasons
 */
export const PhotoReject = (props: PhotoRejectProps) => {
  const { onRetake, photoModerationParams, children } = props;
  const { photoModerationError, photoDenyReasons, photoModResponse } = photoModerationParams;
  const errorMessageText = photoModerationError
    ? "Something went wrong. Please try again."
    : "We're having trouble seeing you in this photo. Please upload a different photo so that your child can easily identify you.";

  // Track photo rejection analytics when component mounts or rejection data changes
  // Includes detailed information about why the photo was rejected
  useEffect(() => {
    if (!photoModResponse) return;

    const rejectReasonsString = photoDenyReasons?.join(", ");
    const results = photoModResponse?.photoModeration?.results || "Reject";

    triggerAnalyticsEvent("sak_engagement_photo_moderation_error", {
      event_action: `${results}:reject`,
      event_category: "sak_photo_upload_error",
      event_label: `${errorMessageText}:${rejectReasonsString}`,
      error_message: `${errorMessageText}:${rejectReasonsString}`,
      error_type: "photo_mod_sak_photo_upload"
    });
  }, [photoModResponse, photoDenyReasons, errorMessageText]);

  return (
    <>
      {children}
      <AlertMessage alertType="danger" visible={true}>
        Your photo could not be added.
      </AlertMessage>
      <p>{errorMessageText}</p>
      <div className="sak-engagement-flow__controls single-button">
        <TourUploadButton
          classList="btn btn-primary photo"
          handleFileChange={onRetake}
          clickEventActionKey="photo_upload_retake_hard_reject"
        >
          Reselect
        </TourUploadButton>
      </div>
    </>
  );
};

export default PhotoReject;
